import * as Device from 'expo-device';
import * as Notifications from 'expo-notifications';
import React from 'react';
import { Platform } from 'react-native';

import {
  addUserNotificationToken,
  deleteUserNotificationToken,
  getUserNotificationToken,
} from '@db/Notifications';
import Constants from '@helpers/Constants';
import { logError, logEvent, logWarning } from '@helpers/Loggers';
import type { AppNavigation } from '@navigation/AppNavigation';
import { NotificationType } from '@types';

import { PushMessage } from '../firebase/functions/src/notifications/common';

type NotificationProps = {
  appNavigation: Pick<AppNavigation, 'openChat' | 'openNotifications'>;
};

export const useNotificationListeners = ({ appNavigation }: NotificationProps) => {
  const notificationListener = React.useRef<Notifications.EventSubscription | null>(null);
  const responseListener = React.useRef<Notifications.EventSubscription | null>(null);

  const onNotificationReceived = React.useCallback((notification: Notifications.Notification) => {
    void notification;
  }, []);
  const onNotificationResponseReceived = React.useCallback(
    (response: Notifications.NotificationResponse) => {
      const { type, entityId } = response.notification.request.content.data as PushMessage['data'];
      if (type === NotificationType.MESSAGE) {
        appNavigation.openChat(entityId);
      } else {
        appNavigation.openNotifications();
      }
    },
    [appNavigation]
  );

  React.useEffect(() => {
    if (Platform.OS === 'web') return;

    Notifications.setNotificationHandler({
      handleNotification: async () => ({
        shouldPlaySound: true,
        shouldSetBadge: true,
        shouldShowBanner: true,
        shouldShowList: true,
      }),
    });

    notificationListener.current =
      Notifications.addNotificationReceivedListener(onNotificationReceived);
    responseListener.current = Notifications.addNotificationResponseReceivedListener(
      onNotificationResponseReceived
    );

    return () => {
      notificationListener.current?.remove();
      responseListener.current?.remove();
    };
  }, [onNotificationReceived, onNotificationResponseReceived]);
};

type NotificationsPermissionsRequestParams = {
  force?: boolean;
};

export const requestNotificationsPermissions = async ({
  force,
}: NotificationsPermissionsRequestParams = {}) => {
  const isAvailable = notificationsAvailable();
  if (isAvailable) {
    // TODO: it may not be necessary to set the notification channel here,
    // as we are already setting it before getting the device push token
    await setNotificationChannel();
    const { status: currentStatus } = await Notifications.getPermissionsAsync();
    if (currentStatus === Notifications.PermissionStatus.UNDETERMINED || force) {
      const { status: newStatus } = await Notifications.requestPermissionsAsync();
      logEvent('notifications_permissions', { status: newStatus });
    } else {
      logWarning('Not requesting notifications permissions (determined)');
    }
  } else {
    logWarning('Not requesting notifications permissions (unavailable)');
  }
};

export const setNotificationChannel = async () => {
  // Android must set notification channel before getting device push token
  // https://docs.expo.dev/versions/latest/sdk/notifications/#android-1
  if (Platform.OS === 'android') {
    await Notifications.setNotificationChannelAsync('default', {
      name: 'default',
      importance: Notifications.AndroidImportance.MAX,
      vibrationPattern: [0, 250, 250, 250],
      lightColor: '#FF231F7C',
    });
  }
};

export async function syncUserNotificationToken() {
  if (Platform.OS === 'web') return;

  const isAvailable = notificationsAvailable();
  if (isAvailable) {
    await setNotificationChannel();
    const permissions = await Notifications.getPermissionsAsync();
    if (permissions.status === Notifications.PermissionStatus.GRANTED) {
      await updateUserNotificationToken();
    } else if (permissions.status === Notifications.PermissionStatus.DENIED) {
      await deleteUserNotificationToken();
    } else if (permissions.status === Notifications.PermissionStatus.UNDETERMINED) {
      logWarning('Not syncing notification token (permission undetermined)');
    } else {
      logError(
        new Error(`Encountered unknown notification permission status: ${permissions.status}`)
      );
    }
  } else {
    logWarning('Not syncing notification token (unavailable)');
  }
}

// TODO: use permissions.ios.status on iOS
// https://docs.expo.dev/versions/v45.0.0/sdk/notifications/#interpreting-the-ios-permissions-response
export async function updateUserNotificationToken() {
  const token = await Notifications.getExpoPushTokenAsync({
    // recommended to explicitly provide projectId
    // ref: https://docs.expo.dev/versions/latest/sdk/notifications/#expopushtokenoptions
    projectId: Constants.eas.projectId,
  });
  const existingToken = await getUserNotificationToken();
  if (token.data !== existingToken) {
    await addUserNotificationToken(token.data);
  }
}

export async function setBadgeCount(count: number) {
  return await Notifications.setBadgeCountAsync(count);
}

export const notificationsAvailable = () => {
  return Device.isDevice && Platform.OS !== 'web';
};
